import noop from 'utils/noop';
import { displayDate, DATE_FORMAT_FULL_WITH_WEEKDAY } from 'utils/date';
import { EmbeddedPreviewContext } from 'modules/templateEditor/EditEmbedded/context/EmbeddedPreviewContext';
import { Placement } from 'modules/placements/types';
import { PreviewPane } from 'modules/templateEditor/EditEmbedded/preview/PreviewPane';
import { UserAvatar } from 'modules/workflows/components/UserAvatar';
import { Divider } from 'ui/divider';
import { JsonView, JsonViewModeContextProvider, ViewTypes } from 'ui/treeView';
import { Body2, Body2Bold } from 'ui/theme';
import { DetailsSectionTitle } from '../styles';
import {
  DetailRow,
  FieldList,
  Header,
  PlacementDetailsContainer,
  Subheader,
  SubheaderField,
  JsonViewWrapper,
  PlacementCreator,
  HeaderContainer,
} from './styles';

export const PlacementDetails = ({ placement }: { placement: Placement }) => {
  const {
    placementName,
    description,
    id,
    createdAt,
    createdBy,
    mediaLayout,
    campaignLimit,
    hasTitle,
    hasBody,
    hasMedia,
    hasDataFeeds,
    textFields,
    hasOpenAction,
    actionButtons,
    payload,
  } = placement;

  const previewContextValues = {
    isLoading: false,
    title: hasTitle ? 'Title' : '',
    body: hasBody ? 'Body' : '',
    actionButtons: actionButtons || [],
    mediaUrl: '',
    hasContent: true,
    previewError: undefined,
    onRefreshPreview: noop,
  };

  const hasPayload = !!Object.keys(payload ?? {}).length;
  const hasContentFields = hasTitle || hasBody || hasMedia || hasDataFeeds;
  const hasCustomTextFields = !!textFields?.length;
  const hasBehavior = hasOpenAction || !!actionButtons?.length || hasPayload;

  return (
    <EmbeddedPreviewContext.Provider value={previewContextValues}>
      <PlacementDetailsContainer data-test="placements-details">
        <HeaderContainer>
          <Header>{placementName}</Header>
          <Subheader>
            <SubheaderField>{`ID: ${id}`}</SubheaderField>
            {description && <SubheaderField>{description}</SubheaderField>}
          </Subheader>
        </HeaderContainer>
        <PreviewPane mediaLayout={mediaLayout} showHeader={false} />
        <DetailsSectionTitle>PLACEMENT</DetailsSectionTitle>
        <DetailRow>
          <Body2Bold>Type</Body2Bold>
          <FieldList>
            {<Body2>{campaignLimit > 1 ? 'Carousel or feed' : 'Banner'}</Body2>}
          </FieldList>
        </DetailRow>
        <DetailRow>
          <Body2Bold>Campaign limit</Body2Bold>
          <FieldList>{<Body2>{campaignLimit}</Body2>}</FieldList>
        </DetailRow>
        {(hasContentFields || hasCustomTextFields || hasBehavior) && (
          <DetailsSectionTitle>DETAILS</DetailsSectionTitle>
        )}
        {hasContentFields && (
          <DetailRow>
            <Body2Bold>Content</Body2Bold>
            <FieldList>
              {hasTitle && <Body2>Title</Body2>}
              {hasBody && <Body2>Body</Body2>}
              {hasMedia && <Body2>Media URL</Body2>}
              {hasDataFeeds && <Body2>Data feed</Body2>}
            </FieldList>
          </DetailRow>
        )}
        {hasCustomTextFields && (
          <DetailRow>
            <Body2Bold>Custom text fields</Body2Bold>
            <FieldList>
              {textFields?.map((field) => (
                <Body2 key={field}>{field}</Body2>
              ))}
            </FieldList>
          </DetailRow>
        )}
        {hasBehavior && (
          <DetailRow>
            <Body2Bold>Behavior</Body2Bold>
            <FieldList>
              {hasOpenAction && <Body2>Open action</Body2>}
              {actionButtons?.map((button, index) => (
                <Body2 key={`action-button-${index}-${button.id}`}>{button.title}</Body2>
              ))}
              {hasPayload && (
                <JsonViewWrapper>
                  <JsonViewModeContextProvider identifier="raw-data" defaultMode={ViewTypes.raw}>
                    <JsonView src={payload} showToggle={false} />
                  </JsonViewModeContextProvider>
                </JsonViewWrapper>
              )}
            </FieldList>
          </DetailRow>
        )}
        {/* TODO: Delivery section hidden until destinations are available */}
        {/* <DetailsSectionTitle>DELIVERY</DetailsSectionTitle>
        <DetailRow>
          <Body2Bold>Destinations</Body2Bold>
          <DestinationList destinations={destinations} />
        </DetailRow> */}
        {createdAt && (
          <>
            <Divider />
            <DetailRow>
              <Body2Bold>Created</Body2Bold>
              <FieldList data-test="placement-create">
                <Body2>{displayDate(createdAt, DATE_FORMAT_FULL_WITH_WEEKDAY)}</Body2>
                <PlacementCreator>
                  <UserAvatar userId={createdBy as string} showFullName />
                </PlacementCreator>
              </FieldList>
            </DetailRow>
          </>
        )}
      </PlacementDetailsContainer>
    </EmbeddedPreviewContext.Provider>
  );
};
